import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiMenu,
  FiX,
  FiUser,
  FiLogOut,
  FiShoppingBag,
  FiCalendar,
  FiCreditCard,
} from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";

const NavbarContainer = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(45, 80, 22, 0.1);
  transition: all 0.3s ease;

  ${(props) =>
    props.$hideOnMobile &&
    `
    @media (max-width: 768px) {
      display: none;
    }
  `}
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d5016;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: #1a3a0e;
  }
`;

const LogoIcon = styled.img`
  width: 180px;
  height: 60px;
  object-fit: contain;
  border-radius: 4px;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: #333;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  position: relative;

  &:hover {
    color: #2d5016;
  }

  &.active {
    color: #2d5016;

    &::after {
      content: "";
      position: absolute;
      bottom: -8px;
      left: 0;
      right: 0;
      height: 2px;
      background: #2d5016;
      border-radius: 1px;
    }
  }
`;

const AuthButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const PrimaryButton = styled(Button)`
  background: #2d5016;
  color: white;
  border: none;

  &:hover {
    background: #1a3a0e;
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: #2d5016;
  border: 1px solid #2d5016;

  &:hover {
    background: #2d5016;
    color: white;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #333;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid rgba(45, 80, 22, 0.1);
  padding: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 999;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MobileNavLink = styled(Link)`
  color: #333;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
  display: block;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: #2d5016;
  }
`;

const MobileLogoutButton = styled.button`
  color: #dc3545;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
  display: block;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: #b02a37;
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #333;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background 0.3s ease;

  &:hover {
    background: #f5f5f5;
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  overflow: hidden;
  z-index: 1001;
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: #333;
  text-decoration: none;
  transition: background 0.3s ease;

  &:hover {
    background: #f5f5f5;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: #dc3545;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #f5f5f5;
  }
`;

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current route is admin or vendor dashboard
  const isDashboardRoute =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/vendor");

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest("[data-user-menu]")) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isUserMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsUserMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <NavbarContainer
        $hideOnMobile={isDashboardRoute}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <NavContent>
          <Logo to="/">
            <LogoIcon src="/images/logo.png" alt="NourishGY Logo" />
          </Logo>

          <NavLinks>
            <NavLink to="/" className={isActive("/") ? "active" : ""}>
              Home
            </NavLink>
            <NavLink
              to="/vendors"
              className={isActive("/vendors") ? "active" : ""}
            >
              Vendors
            </NavLink>
            <NavLink
              to="/meal-plans"
              className={isActive("/meal-plans") ? "active" : ""}
            >
              Meal Plans
            </NavLink>
            <NavLink
              to="/meals"
              className={isActive("/meals") ? "active" : ""}
            >
              Meals
            </NavLink>
            <NavLink to="/about" className={isActive("/about") ? "active" : ""}>
              About
            </NavLink>
            <NavLink
              to="/contact"
              className={isActive("/contact") ? "active" : ""}
            >
              Contact
            </NavLink>

            {/* Dashboard link for vendors, admins, and employees */}
            {isAuthenticated &&
              (user?.role === "vendor" ||
                user?.role === "admin" ||
                user?.role === "employee") && (
                <NavLink
                  to={user?.role === "vendor" ? "/vendor" : "/admin"}
                  className={
                    isActive(user?.role === "vendor" ? "/vendor" : "/admin")
                      ? "active"
                      : ""
                  }
                  style={{
                    background: "linear-gradient(135deg, #2d5016, #4a7c59)",
                    color: "white",
                    fontWeight: "600",
                    borderRadius: "6px",
                    padding: "0.5rem 1rem",
                    marginLeft: "0.5rem",
                  }}
                >
                  {user?.role === "vendor"
                    ? "Vendor Dashboard"
                    : "Admin Dashboard"}
                </NavLink>
              )}
          </NavLinks>

          <AuthButtons>
            {isAuthenticated ? (
              <UserMenu data-user-menu>
                <UserButton onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}>
                  <FiUser />
                  {user?.firstName || user?.first_name || user?.name || "User"}
                </UserButton>

                <AnimatePresence>
                  {isUserMenuOpen && (
                    <DropdownMenu
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <DropdownItem to="/profile">
                        <FiUser />
                        Profile
                      </DropdownItem>
                      <DropdownItem to="/orders">
                        <FiShoppingBag />
                        My Orders
                      </DropdownItem>
                      <DropdownItem to="/subscriptions">
                        <FiCalendar />
                        Subscriptions
                      </DropdownItem>
                      <DropdownItem to="/payments">
                        <FiCreditCard />
                        Payments
                      </DropdownItem>
                      <LogoutButton onClick={handleLogout}>
                        <FiLogOut />
                        Logout
                      </LogoutButton>
                    </DropdownMenu>
                  )}
                </AnimatePresence>
              </UserMenu>
            ) : (
              <>
                <SecondaryButton as={Link} to="/login">
                  Login
                </SecondaryButton>
                <PrimaryButton as={Link} to="/register">
                  Sign Up
                </PrimaryButton>
              </>
            )}
          </AuthButtons>

          <MobileMenuButton
            onClick={() => {
              console.log(
                "Mobile menu button clicked, current state:",
                isMobileMenuOpen
              );
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
          >
            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
          </MobileMenuButton>
        </NavContent>
      </NavbarContainer>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            style={{ display: "block" }}
          >
            <MobileNavLinks>
              <MobileNavLink to="/">Home</MobileNavLink>
              <MobileNavLink to="/vendors">Vendors</MobileNavLink>
              <MobileNavLink to="/meal-plans">Meal Plans</MobileNavLink>
              <MobileNavLink to="/meals">Meals</MobileNavLink>
              <MobileNavLink to="/about">About</MobileNavLink>
              <MobileNavLink to="/contact">Contact</MobileNavLink>

              {isAuthenticated ? (
                <>
                  <MobileNavLink
                    style={{
                      color: "#2d5016",
                      fontWeight: "600",
                      borderBottom: "1px solid #e0e0e0",
                      marginBottom: "0.5rem",
                      paddingBottom: "0.5rem",
                    }}
                  >
                    Welcome,{" "}
                    {user?.firstName ||
                      user?.first_name ||
                      user?.name ||
                      "User"}
                    !
                  </MobileNavLink>

                  {/* Dashboard link for vendors, admins, and employees */}
                  {(user?.role === "vendor" ||
                    user?.role === "admin" ||
                    user?.role === "employee") && (
                    <MobileNavLink
                      to={user?.role === "vendor" ? "/vendor" : "/admin"}
                      style={{
                        background: "linear-gradient(135deg, #2d5016, #4a7c59)",
                        color: "white",
                        fontWeight: "600",
                        borderRadius: "6px",
                        margin: "0.5rem 0",
                        textAlign: "center",
                      }}
                    >
                      {user?.role === "vendor"
                        ? "Vendor Dashboard"
                        : "Admin Dashboard"}
                    </MobileNavLink>
                  )}

                  <MobileNavLink to="/profile">Profile</MobileNavLink>
                  <MobileNavLink to="/orders">My Orders</MobileNavLink>
                  <MobileNavLink to="/subscriptions">
                    Subscriptions
                  </MobileNavLink>
                  <MobileNavLink to="/payments">Payments</MobileNavLink>
                  <MobileLogoutButton onClick={handleLogout}>
                    Logout
                  </MobileLogoutButton>
                </>
              ) : (
                <>
                  <MobileNavLink to="/login">Login</MobileNavLink>
                  <MobileNavLink to="/register">Sign Up</MobileNavLink>
                </>
              )}
            </MobileNavLinks>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
